import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { stageMovementsApi } from '../api/stageMovements';
import { leadsApi } from '../api/leads';
import { accountsApi } from '../api/accounts';
import PremiumLayout from '../components/PremiumLayout';
import { ArrowRightIcon, ClockIcon, BuildingOfficeIcon, UserIcon, ChartBarIcon } from '@heroicons/react/24/outline';

// Stage definitions with colors
const stages = [
  { id: 'new', name: 'New', color: 'bg-slate-500' },
  { id: 'contacted', name: 'Contacted', color: 'bg-blue-500' },
  { id: 'first_meeting', name: 'First Meeting', color: 'bg-cyan-500' },
  { id: 'second_meeting', name: 'Second Meeting', color: 'bg-teal-500' },
  { id: 'qualified', name: 'Qualified', color: 'bg-emerald-500' },
  { id: 'pitch_presented', name: 'Pitch Presented', color: 'bg-amber-500' },
  { id: 'sow_in_progress', name: 'SOW in Progress', color: 'bg-orange-500' },
  { id: 'sow_sent', name: 'SOW Sent', color: 'bg-pink-500' },
  { id: 'clarification_call', name: 'Clarification Call', color: 'bg-fuchsia-500' },
  { id: 'roi_proof_call', name: 'ROI / Proof Call', color: 'bg-purple-500' },
  { id: 'decision_stage', name: 'Decision Stage', color: 'bg-violet-500' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-indigo-500' },
  { id: 'won', name: 'Won', color: 'bg-green-500' },
  { id: 'lost', name: 'Lost', color: 'bg-red-500' },
];

const getStageInfo = (stageId: string) => {
  return stages.find(s => s.id === stageId) || { id: stageId, name: stageId, color: 'bg-gray-500' };
};

const StageMovements: React.FC = () => {
  const navigate = useNavigate();
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  const { data: movements = [], isLoading } = useQuery({
    queryKey: ['stage-movements', selectedLeadId],
    queryFn: () => selectedLeadId ? stageMovementsApi.getByLeadId(selectedLeadId) : stageMovementsApi.getAll(),
  });

  // Get selected lead details
  const selectedLead = useMemo(() => {
    return selectedLeadId ? leads.find(l => l.id === selectedLeadId) : null;
  }, [selectedLeadId, leads]);

  // Build journey timeline for selected lead
  const journeyTimeline = useMemo(() => {
    if (!selectedLeadId || movements.length === 0) return [];
    
    // Sort movements chronologically (oldest first)
    const sortedMovements = [...movements].sort((a, b) => 
      new Date(a.moved_at).getTime() - new Date(b.moved_at).getTime()
    );

    // Build timeline with all stages the lead has been through
    const timeline: Array<{ stage: string; date: Date; isCurrent: boolean; movement?: any }> = [];
    
    sortedMovements.forEach((movement, index) => {
      const fromStage = movement.from_stage || 'new';
      const toStage = movement.to_stage;
      const moveDate = new Date(movement.moved_at);
      
      // Add from stage if it's the first movement or different from previous
      if (index === 0 || (index > 0 && sortedMovements[index - 1].to_stage !== fromStage)) {
        timeline.push({
          stage: fromStage,
          date: index === 0 ? (selectedLead?.created_at ? new Date(selectedLead.created_at) : moveDate) : moveDate,
          isCurrent: false,
        });
      }
      
      // Add to stage
      timeline.push({
        stage: toStage,
        date: moveDate,
        isCurrent: index === sortedMovements.length - 1,
        movement,
      });
    });

    return timeline;
  }, [selectedLeadId, movements, selectedLead]);

  // Get lead info for each movement
  const getLeadInfo = (leadId: number) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return { brand: 'Unknown', contact: 'Unknown' };
    const contactName = lead.first_name && lead.last_name
      ? `${lead.first_name} ${lead.last_name}`
      : lead.first_name || lead.lead_name || 'No Contact';
    return {
      brand: lead.brand || lead.name || 'Unknown',
      contact: contactName,
    };
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Stage Movements">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Stage Movements"
      subtitle="Track lead stage transitions and history"
    >
      <div className="space-y-6">
        {/* Lead Selector */}
        <div className="premium-card p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Lead to View Journey
          </label>
          <select
            value={selectedLeadId || ''}
            onChange={(e) => setSelectedLeadId(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">-- Select a lead to view journey --</option>
            {leads.map((lead) => {
              // Get account name if available (company/account name is priority)
              const account = accounts.find(a => a.id === lead.account_id);
              const companyName = account?.name || lead.project_name || lead.brand || lead.name;
              const contactName = lead.first_name && lead.last_name
                ? `${lead.first_name} ${lead.last_name}`
                : lead.first_name || lead.lead_name || '';
              return (
                <option key={lead.id} value={lead.id}>
                  {companyName} {contactName ? `(${contactName})` : ''} - {lead.stage}
                </option>
              );
            })}
          </select>
        </div>

        {/* Selected Lead Journey Timeline */}
        {selectedLead && journeyTimeline.length > 0 && (
          <div className="premium-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <ChartBarIcon className="h-6 w-6 mr-2 text-indigo-600" />
                  Journey Timeline: {selectedLead.brand || selectedLead.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {(() => {
                    const contactName = selectedLead.first_name && selectedLead.last_name
                      ? `${selectedLead.first_name} ${selectedLead.last_name}`
                      : selectedLead.first_name || selectedLead.lead_name || '';
                    return contactName ? `Contact: ${contactName}` : '';
                  })()}
                  {selectedLead.lead_name && selectedLead.industry && ' • '}
                  {selectedLead.industry && `Industry: ${selectedLead.industry}`}
                </p>
              </div>
              <button
                onClick={() => navigate(`/leads/${selectedLead.id}`)}
                className="btn-secondary text-sm"
              >
                View Lead Details →
              </button>
            </div>
            
            {/* Visual Timeline */}
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              
              <div className="space-y-6">
                {journeyTimeline.map((item, index) => {
                  const stageInfo = getStageInfo(item.stage);
                  return (
                    <div key={index} className="relative flex items-start space-x-4">
                      {/* Stage Dot */}
                      <div className={`relative z-10 w-8 h-8 rounded-full ${stageInfo.color} flex items-center justify-center shadow-lg ${
                        item.isCurrent ? 'ring-4 ring-indigo-200 ring-offset-2' : ''
                      }`}>
                        <span className="text-white text-xs font-bold">
                          {index + 1}
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 pb-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white ${stageInfo.color}`}>
                              {stageInfo.name}
                            </span>
                            {item.isCurrent && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                Current Stage
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">
                            {item.date.toLocaleDateString('en-IN', { 
                              day: '2-digit', 
                              month: 'short', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        {item.movement?.reason && (
                          <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                            {item.movement.reason}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* All Movements List (when no lead selected) */}
        {!selectedLeadId && (
          <div className="premium-card p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">All Stage Movements</h2>
            {movements.length === 0 ? (
              <div className="text-center py-12">
                <ClockIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No stage movements found</p>
                <p className="text-sm text-gray-400 mt-2">Select a lead above to view its journey</p>
              </div>
            ) : (
              <div className="space-y-4">
                {movements.map((movement) => {
                  const leadInfo = getLeadInfo(movement.lead_id);
                  const fromStageInfo = getStageInfo(movement.from_stage || 'new');
                  const toStageInfo = getStageInfo(movement.to_stage);
                  
                  return (
                    <div
                      key={movement.id}
                      className="border-l-4 border-indigo-500 pl-4 py-3 bg-gray-50 rounded-r-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => setSelectedLeadId(movement.lead_id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <BuildingOfficeIcon className="h-5 w-5 text-indigo-500" />
                          <div>
                            <div className="font-semibold text-gray-900">{leadInfo.brand}</div>
                            <div className="text-xs text-gray-500 flex items-center">
                              <UserIcon className="h-3 w-3 mr-1" />
                              {leadInfo.contact}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(movement.moved_at).toLocaleDateString('en-IN', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${fromStageInfo.color}`}>
                          {fromStageInfo.name}
                        </span>
                        <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${toStageInfo.color}`}>
                          {toStageInfo.name}
                        </span>
                      </div>
                      {movement.reason && (
                        <p className="text-sm text-gray-600 mt-2">{movement.reason}</p>
                      )}
                      <p className="text-xs text-indigo-600 mt-2">Click to view full journey →</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default StageMovements;
